import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Chip, DialogContent, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { DistaDialog, DistaDialogHeader } from "@sista/dista-ui";
import { IdmClientContext } from "@sista/idm-client";
import { formatTimestamp, usePagedListing, useSafeSistaAsync, } from "@sista/library-stasi";
import { SistaChip } from "@sista/sista-chip";
import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSpravaTextuClient } from "../../util/useSpravaTextuClient";
const pageSize = 50;
const maxRows = 500;
/** Jedna hodnota historie - prázdný řetězec znamená, že text v daném jazyce (ještě) neexistoval. */
const HistoryValue = ({ value, isOldValue, }) => {
    const { t } = useTranslation("sprava-textu");
    if (value === "") {
        return (_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { fontStyle: "italic" }, children: t("sprava-textu.textHistory.emptyValue", "(prázdné)") }));
    }
    return (_jsx(Typography, { variant: "body2", sx: {
            wordBreak: "break-word",
            ...(isOldValue
                ? { color: "text.secondary", textDecoration: "line-through" }
                : { fontWeight: "bold" }),
        }, children: value }));
};
const HistoryChange = ({ row }) => {
    const { t } = useTranslation("sprava-textu");
    const isTextCreated = row.oldValue === "" && row.newValue !== "";
    const isTextDeleted = row.newValue === "";
    return (_jsxs(Stack, { direction: "column", spacing: 0.5, sx: { py: 1, width: "100%" }, children: [(isTextCreated || isTextDeleted) && (_jsx(Box, { children: _jsx(Chip, { size: "small", color: isTextDeleted ? "error" : "success", label: isTextDeleted
                        ? t("sprava-textu.textHistory.textDeleted", "Smazáno")
                        : t("sprava-textu.textHistory.textCreated", "Vytvořeno") }) })), _jsx(HistoryValue, { value: row.oldValue, isOldValue: true }), _jsx(HistoryValue, { value: row.newValue })] }));
};
const NoHistoryOverlay = () => {
    const { t } = useTranslation("sprava-textu");
    return (_jsx(Stack, { direction: "column", sx: { height: "100%", justifyContent: "center", alignItems: "center" }, children: _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { p: 2 }, children: t("sprava-textu.textHistory.noHistory", "Pro tento text zatím není zaznamenaná žádná změna. Historie se vede až od nasazení této funkčnosti, u starších textů může být prázdná.") }) }));
};
export const TextHistoryDialog = ({ namespace, textKey, onClose, }) => {
    const { t } = useTranslation("sprava-textu");
    const spratex = useSpravaTextuClient();
    const { loggedAccount } = useContext(IdmClientContext);
    const [getTextHistoryCall] = useSafeSistaAsync(spratex.getTextHistory);
    const loader = useCallback(async (page) => {
        const result = await getTextHistoryCall({
            namespace: namespace,
            key: textKey,
            page: page,
            pageSize: pageSize,
        });
        if (result == null) {
            return [];
        }
        return result;
    }, [getTextHistoryCall, namespace, textKey]);
    const { currentPage, pending, rows } = usePagedListing({
        loader,
        maxRows,
    });
    // TextHistoryDTO nemá vlastní identifikátor, pořadí řádků je dané serverem (od nejnovější změny).
    const historyRows = useMemo(() => {
        return rows.map((row, index) => ({ ...row, id: index }));
    }, [rows]);
    const columns = useMemo(() => {
        return [
            {
                field: "dateCreated",
                headerName: t("sprava-textu.textHistory.dateCreated", "Datum změny"),
                flex: 1.2,
                valueGetter: (params) => {
                    if (params.row.dateCreated == null)
                        return "";
                    return formatTimestamp(params.row.dateCreated);
                },
            },
            {
                field: "lang",
                headerName: t("sprava-textu.textHistory.lang", "Jazyk"),
                width: 90,
                renderCell: (params) => (_jsx(Chip, { size: "small", label: params.row.lang })),
            },
            {
                field: "context",
                headerName: t("sprava-textu.textHistory.context", "Varianta"),
                flex: 2,
                sortable: false,
                renderCell: (params) => (params.row.context?.length ?? 0) === 0 ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("sprava-textu.textHistory.defaultVariant", "Výchozí") })) : (_jsx(Stack, { direction: "row", spacing: 0.5, sx: { py: 1, flexWrap: "wrap", gap: 0.5 }, children: params.row.context.map((urn) => (_jsx(SistaChip, { variant: "small", urn: urn, authToken: loggedAccount.authKey }, urn))) })),
            },
            {
                field: "change",
                headerName: t("sprava-textu.textHistory.change", "Změna"),
                flex: 3,
                sortable: false,
                renderCell: (params) => (_jsx(HistoryChange, { row: params.row })),
            },
            {
                field: "authorUrn",
                headerName: t("sprava-textu.textHistory.author", "Autor"),
                flex: 1.5,
                sortable: false,
                renderCell: (params) => params.row.authorUrn == null ? null : (_jsx(SistaChip, { variant: "small", urn: params.row.authorUrn, authToken: loggedAccount.authKey })),
            },
        ];
    }, [t, loggedAccount.authKey]);
    return (_jsxs(DistaDialog, { open: true, onClose: onClose, maxWidth: "lg", children: [_jsx(DistaDialogHeader, { title: t("sprava-textu.textHistory.title", "Historie změn textu"), onClose: onClose }), _jsxs(DialogContent, { children: [_jsxs(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: [namespace, " / ", textKey] }), _jsx(Box, { sx: { height: "60vh" }, children: _jsx(DataGridPro, { columns: columns, rows: historyRows, getRowHeight: () => "auto", disableRowSelectionOnClick: true, pagination: true, pageSizeOptions: [pageSize], loading: currentPage < 1 && pending, slots: { noRowsOverlay: NoHistoryOverlay } }) })] })] }));
};
